import {action, autorun, computed, makeObservable, observable, toJS} from 'mobx';

import {ExecutionEnvironment} from './InterfaceStore';
import {deleteCookie, getCookieFromString} from "../utils/StringUtilities";
import {getCdnUrl} from "../utils/SchoolBlocksUtilities";

const _localStoragekey = "userStore";

export type IUserStoreInitData = {
    user: SoprisAppsProps["pageProps"]["user"],
    currentOrganization: SoprisAppsProps["pageProps"]["currentOrganization"],
}

export default class UserStore {
    id = null;
    username = null;
    firstName = null;
    lastName = null;
    _avatar: string | null = null;
    admin = false;
    viewer = false;
    editor = false;
    follower = false;
    unreadNotificationsCount = 0;
    initData: IUserStoreInitData | null = null;

    supportChatOpen = false;
    initialized = false;

    constructor(initData: IUserStoreInitData | undefined) {
        this.initialize(initData);

        makeObservable(this, {
            id: observable,
            username: observable,
            firstName: observable,
            lastName: observable,
            _avatar: observable,
            admin: observable,
            viewer: observable,
            editor: observable,
            follower: observable,
            loadFromClientStorage: action,
            updateUser: action,
            setPermissions: action,
            logout: action,
            isAdmin: computed,
            isEditor: computed,
            unreadNotificationsCount: observable,
            setUnreadNotificationsCount: action,

            supportChatEnabled: computed,
            supportChatOpen: observable,
            openSupportChat: action,
            closeSupportChat: action,
        });
        if (ExecutionEnvironment.canUseDOM && window.localStorage) {
            this.loadFromClientStorage();
            autorun(() => {
                // This code will run every time any observable property on the store is updated.
                const mobxJson = JSON.stringify(toJS({
                    id: this.id,
                    username: this.username,
                    firstName: this.firstName,
                    lastName: this.lastName,
                    avatar: this.avatar,
                    admin: this.admin,
                    editor: this.editor,
                    viewer: this.viewer,
                    follower: this.follower,
                }));
                localStorage.setItem(_localStoragekey, mobxJson);
            });
        }
    }

    initialize = (initData: IUserStoreInitData | undefined) => {
        if (initData) {
            this.initData = {
                user: initData.user,
                currentOrganization: initData.currentOrganization,
            };

            if (initData.user) {
                this.updateUser(initData.user);
            }

            this.setPermissions(initData.currentOrganization.user_permissions);
            this.initialized = true;
        }
    }

    serialize: () => IUserStoreInitData = () => {
        return {
            user: (this.initData as IUserStoreInitData).user,
            currentOrganization: (this.initData as IUserStoreInitData).currentOrganization,
        }
    }

    loadFromClientStorage() {
        if (ExecutionEnvironment.canUseDOM && window.localStorage) {
            const mobxJson = localStorage.getItem(_localStoragekey);
            // presence of a valid token is required for authenticated API calls to succeed
            // should only load user if token is present
            const token = getCookieFromString("token", document.cookie);
            if (mobxJson && token) {
                const user = JSON.parse(mobxJson);
                this.id = user.id;
                this.username = user.username;
                this.firstName = user.firstName;
                this.lastName = user.lastName;
                this._avatar = user.avatar;
                this.admin = user.admin;
                this.editor = user.editor;
                this.viewer = user.viewer;
                this.follower = user.follower;
                this.supportChatOpen = user.supportChatOpen || false;
            }
        }
    }

    get isAdmin() {
        return this.admin || false;
    }

    get isEditor() {
        return this.editor || false;
    }

    get avatar() {
        return getCdnUrl(this._avatar || "");
    }

    openSupportChat() {
        this.supportChatOpen = true;
    }

    closeSupportChat() {
        this.supportChatOpen = false;
    }

    get supportChatEnabled() {
        return this.isAdmin || this.isEditor;
    }

    updateUser(user) {
        user = user || {};

        if (user.hasOwnProperty('id')) {
            this.id = user.id;
        }

        if (user.hasOwnProperty('username')) {
            this.username = user.username;
        }

        if (user.hasOwnProperty('fname')) {
            this.firstName = user.fname;
        }

        if (user.hasOwnProperty('lname')) {
            this.lastName = user.lname;
        }

        if (user.hasOwnProperty('avatar')) {
            this._avatar = user.avatar;
        }
    }

    setPermissions = permissions => {
        // loop through because permissions object may not contain all permission levels
        for (let k in permissions) {
            if (permissions.hasOwnProperty(k)) {
                this[k] = permissions[k];
            }
        }
    };

    setUnreadNotificationsCount = (count) => {
        this.unreadNotificationsCount = count;
    }

    logout = async () => {
        this.id = null;
        this.username = null;
        this.firstName = null;
        this.lastName = null;
        this._avatar = null;
        this.admin = false;
        this.editor = false;
        this.follower = false;
        this.supportChatOpen = false;
        await deleteCookie("token");
        await deleteCookie("context")

        if (typeof MC !== "undefined") {
            MC.reactReauth(null);
        }
    }
}

